import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trophy, CheckCircle2, XCircle, MinusCircle, Clock, TrendingUp, Target, GitCompare, Users, Sparkles, RotateCcw, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TypeBadge, PatternBadge, Crumb, err, fmtTime, startTest, trackEvent } from "./shared";

export default function PrepResult() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [recs, setRecs] = useState([]);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get(`/test-prep/attempts/${attemptId}/result`).then((r) => {
      setA(r.data); setAi(r.data.ai_summary || null);
      trackEvent("result_viewed", { attempt_id: attemptId, exam_id: r.data.exam_id });
      api.get(`/test-prep/recommendations?exam_id=${r.data.exam_id}`).then((x) => setRecs(x.data.recommendations)).catch(() => {});
    }).catch((e) => { toast.error(err(e)); navigate("/prep/history"); });
  }, [attemptId, navigate]);

  if (!a) return <div className="p-8 text-slate-400">Loading result...</div>;
  const r = a.result, re = a.rank_estimate || {};
  const premium = user?.plan && user.plan !== "free";

  const getAi = async () => {
    setAiBusy(true);
    try { const { data } = await api.post(`/test-prep/attempts/${attemptId}/ai-summary`); setAi(data.summary); }
    catch (e) { toast.error(err(e)); } finally { setAiBusy(false); }
  };
  const onRec = (rec) => { trackEvent("recommendation_clicked", { test_id: rec.test_id, exam_id: a.exam_id }); startTest(navigate, rec.test_id, true); };

  return (
    <div data-testid="prep-result" className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Crumb items={[{ to: "/prep", label: "Test Preparation" }, { to: `/prep/exam/${a.exam_id}`, label: a.exam_name }, { label: "Result" }]} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2"><TypeBadge type={a.test_type} /><PatternBadge status={a.pattern_status} />{r.auto_submitted && <span className="rounded-full bg-rose-400/10 px-2.5 py-1 text-[11px] font-semibold text-rose-300">Auto-submitted (time over)</span>}</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight">{a.title}</h1>
          <p className="mt-1 text-xs text-slate-500">Submitted {new Date(a.submitted_at).toLocaleString()}</p>
        </div>
        <button data-testid="retake-btn" onClick={() => startTest(navigate, a.test_id)} className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2.5 text-sm font-semibold text-cyan-300 hover:bg-cyan-400/15"><RotateCcw className="h-4 w-4" /> Retake</button>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Big label="Score" value={`${r.score}`} sub={`of ${r.total_marks}`} testid="result-score" tone="cyan" className="col-span-2 md:col-span-2" />
        <Small icon={CheckCircle2} label="Correct" value={r.correct} tone="emerald" testid="result-correct" />
        <Small icon={XCircle} label="Incorrect" value={r.incorrect} tone="rose" testid="result-incorrect" />
        <Small icon={MinusCircle} label="Unattempted" value={r.unattempted} tone="slate" testid="result-unattempted" />
        <Small icon={Target} label="Accuracy" value={`${r.accuracy}%`} tone="amber" testid="result-accuracy" />
        <Small icon={Clock} label="Time" value={fmtTime(r.time_taken_sec)} sub={`${r.avg_time_per_question_sec}s / Q`} tone="indigo" testid="result-time" />
      </motion.div>

      {/* Rank + college connection (existing engines) */}
      <section data-testid="rank-connection" className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-5">
        <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-cyan-400" /><h2 className="text-base font-bold md:text-lg">Rank & college intelligence</h2></div>
        {re.available ? (
          <>
            <p className="mt-1 text-xs text-amber-300">{re.label}. Not an official result.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Big label="Estimated rank" value={re.predicted_rank.toLocaleString()} sub={`Range ${re.rank_range[0].toLocaleString()}–${re.rank_range[1].toLocaleString()}`} testid="rank-estimate" tone="cyan" />
              <Big label="Estimated percentile" value={re.percentile} testid="percentile-estimate" tone="emerald" />
              <Big label="Confidence" value={`${re.confidence}%`} testid="rank-confidence" tone="amber" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Cta to={`/predictor/rank?exam=${encodeURIComponent(re.exam_key)}&score=${Math.max(0, r.score)}`} icon={TrendingUp} label="Open Rank Predictor" testid="cta-rank-predictor" />
              {re.college_exam_key && <Cta to={`/predictor/college?exam=${encodeURIComponent(re.college_exam_key)}&rank=${re.predicted_rank}`} icon={Target} label="See colleges for my estimated rank" testid="cta-college-predictor" />}
              <Cta to="/compare" icon={GitCompare} label="Compare colleges" testid="cta-compare" />
              <Cta to="/counsellors" icon={Users} label="Talk to a verified counsellor" testid="cta-counsellors" />
            </div>
          </>
        ) : (
          <>
            <p data-testid="rank-unavailable" className="mt-1 text-sm text-slate-400">{re.reason || "Rank prediction is currently unavailable for this test."}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Cta to="/predictor/rank" icon={TrendingUp} label="Rank Predictor" testid="cta-rank-predictor" />
              <Cta to="/predictor/college" icon={Target} label="College Predictor" testid="cta-college-predictor" />
              <Cta to="/counsellors" icon={Users} label="Talk to a verified counsellor" testid="cta-counsellors" />
            </div>
          </>
        )}
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <section className="rounded-2xl border border-white/8 bg-[#131C2E] p-5 lg:col-span-2">
          <h2 className="text-base font-bold md:text-lg">Performance summary</h2>
          <Breakdown title="By subject" rows={r.by_subject} testid="breakdown-subject" />
          <Breakdown title="By topic" rows={r.by_topic} testid="breakdown-topic" />
          <Breakdown title="By difficulty" rows={r.by_difficulty} testid="breakdown-difficulty" />
          <Insights r={r} />
        </section>

        <div className="space-y-6">
          <section className="rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-5">
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" /><h2 className="font-bold">Recommended next step</h2></div>
            <div className="mt-3 space-y-2">
              {recs.map((rec) => (
                <button key={rec.test_id} data-testid={`result-rec-${rec.test_type}`} onClick={() => onRec(rec)} className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left hover:border-cyan-400/40">
                  <p className="text-sm font-semibold">{rec.title}</p><p className="mt-0.5 text-xs text-slate-400">{rec.reason}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-cyan-300">Start <ArrowRight className="h-3 w-3" /></p>
                </button>
              ))}
            </div>
          </section>
          <section className="rounded-2xl border border-white/8 bg-[#131C2E] p-5">
            <h2 className="font-bold">AI coaching summary</h2>
            {ai ? <p data-testid="ai-summary" className="mt-2 whitespace-pre-line text-sm text-slate-300">{ai}</p> : (
              <>
                <p className="mt-1 text-xs text-slate-400">{premium ? "One AI call, cached for this result." : "Premium feature — upgrade to get a personalised explanation of this result."}</p>
                {premium ? <button data-testid="ai-summary-btn" onClick={getAi} disabled={aiBusy} className="mt-3 inline-flex items-center gap-1 rounded-full bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016] disabled:opacity-50">{aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Explain my performance</button>
                  : <Link to="/pricing" data-testid="ai-summary-upgrade" className="mt-3 inline-flex rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-2 text-xs font-bold text-amber-300">Upgrade</Link>}
              </>
            )}
          </section>
        </div>
      </div>

      <section className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-5">
        <div className="flex items-center justify-between"><h2 className="text-base font-bold md:text-lg">Question review</h2>
          <button data-testid="toggle-review-btn" onClick={() => setShowAll(!showAll)} className="text-xs text-cyan-300">{showAll ? "Show incorrect only" : "Show all"}</button></div>
        <div className="mt-3 space-y-3">
          {a.questions.map((q, i) => {
            const pq = r.per_question[i];
            if (!showAll && pq.is_correct === true) return null;
            return (
              <div key={q.qid} data-testid="review-question" className={`rounded-xl border p-4 ${pq.is_correct === true ? "border-emerald-400/20" : pq.is_correct === false ? "border-rose-400/20" : "border-white/10"}`}>
                <p className="text-xs text-slate-500">{q.n}. {q.subject} · {q.topic} · <span className="capitalize">{q.difficulty}</span> · {pq.marks_awarded > 0 ? "+" : ""}{pq.marks_awarded} marks · {pq.time_spent_sec}s</p>
                <p className="mt-1 text-sm">{q.text}</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {q.options.map((o, k) => (
                    <li key={k} className={`rounded-lg px-3 py-1.5 ${k === pq.correct_index ? "bg-emerald-400/10 text-emerald-300" : k === pq.answer ? "bg-rose-400/10 text-rose-300" : "text-slate-400"}`}>{String.fromCharCode(65 + k)}. {o}{k === pq.correct_index && " ✓"}{k === pq.answer && k !== pq.correct_index && " (your answer)"}</li>
                  ))}
                </ul>
                {q.explanation && <p className="mt-2 text-xs text-slate-400"><span className="font-semibold text-slate-300">Explanation:</span> {q.explanation}</p>}
                {q.source && <p className="mt-1 text-[11px] text-slate-600">Source: {q.source}</p>}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

const Big = ({ label, value, sub, testid, tone, className = "" }) => (
  <div data-testid={testid} className={`rounded-2xl border border-${tone}-400/30 bg-${tone}-400/5 p-5 text-center ${className}`}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`mt-1 font-mono-data text-3xl font-extrabold text-${tone}-300`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);
const Small = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/8 bg-[#131C2E] p-4">
    <Icon className={`h-4 w-4 text-${tone}-300`} /><p className="mt-2 text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="font-mono-data text-xl font-extrabold">{value}</p>{sub && <p className="text-[11px] text-slate-500">{sub}</p>}
  </div>
);
const Cta = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} data-testid={testid} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold hover:border-cyan-400/40 hover:text-cyan-300"><Icon className="h-3.5 w-3.5" /> {label}</Link>
);
const Breakdown = ({ title, rows, testid }) => rows?.length ? (
  <div className="mt-4" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</p>
    <div className="mt-2 space-y-2">
      {rows.map((b) => {
        const acc = b.accuracy ?? 0;
        return (
          <div key={b.id} className="text-sm">
            <div className="flex justify-between"><span>{b.name}</span><span className="font-mono-data text-xs text-slate-400">{b.correct}/{b.attempted} correct{b.unattempted ? ` · ${b.unattempted} skipped` : ""} · {b.accuracy == null ? "—" : `${acc}%`}</span></div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5"><div className={`h-full ${acc >= 75 ? "bg-emerald-400" : acc >= 50 ? "bg-amber-400" : "bg-rose-400"}`} style={{ width: `${acc}%` }} /></div>
          </div>
        );
      })}
    </div>
  </div>
) : null;

const Insights = ({ r }) => {
  const topics = r.by_topic.filter((b) => b.attempted > 0);
  const strong = topics.filter((b) => b.accuracy >= 75).map((b) => b.name);
  const weak = topics.filter((b) => b.accuracy < 50).map((b) => b.name);
  const slow = r.per_question.filter((p) => p.time_spent_sec > Math.max(90, 2 * (r.avg_time_per_question_sec || 0))).length;
  if (!topics.length) return null;
  return (
    <div data-testid="result-insights" className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Where marks were won and lost</p>
      {strong.length > 0 && <p className="mt-2"><span className="text-emerald-300">Strong:</span> {strong.join(", ")}</p>}
      {weak.length > 0 && <p className="mt-1"><span className="text-rose-300">Losing marks in:</span> {weak.join(", ")}</p>}
      {r.unattempted > 0 && <p className="mt-1 text-slate-400">{r.unattempted} question{r.unattempted > 1 ? "s" : ""} left unattempted.</p>}
      {slow > 0 && <p className="mt-1 text-slate-400">Time management: {slow} question{slow > 1 ? "s" : ""} took far longer than your average.</p>}
    </div>
  );
};
